import React, { useState } from 'react';

const DealerInsightApp = () => {
  // Main navigation state
  const [activeScreen, setActiveScreen] = useState('home');
  // Tab navigation state (for vehicle details)
  const [activeTab, setActiveTab] = useState('overview');
  // Scan input state
  const [scanInput, setScanInput] = useState('');
  
  // Dealer info
  const dealerInfo = {
    name: "Herman Meledje",
    dealership: "Atlanta Motors",
    location: "Atlanta, GA",
    role: "Sales Manager",
    dealershipLogo: "AM",
    notifications: 3,
    joinDate: "May 2019",
    salesThisMonth: 12,
    profitThisMonth: "$24,560",
    profileImage: "HM"
  };
  
  // Inventory data
  const inventoryData = [
    {
      id: "inv001",
      year: 2019,
      make: "Toyota",
      model: "Camry SE",
      price: 16980,
      mileage: 45233,
      image: "TC",
      daysInInventory: 12,
      profit: 2340
    },
    {
      id: "inv002",
      year: 2020,
      make: "Honda",
      model: "Civic LX",
      price: 18450,
      mileage: 31205,
      image: "HC",
      daysInInventory: 5,
      profit: 2120
    },
    {
      id: "inv003",
      year: 2017,
      make: "Nissan",
      model: "Rogue SV",
      price: 14795,
      mileage: 64127,
      image: "NR",
      daysInInventory: 18,
      profit: 1880
    },
    {
      id: "inv004",
      year: 2014,
      make: "Honda",
      model: "Accord EX-L",
      price: 5180,
      mileage: 277502,
      image: "HA",
      daysInInventory: 2,
      profit: 2470,
      isActive: true
    }
  ];
  
  // Recent scan history
  const recentScans = [
    {
      vin: "1HGCR2F81EA036925",
      year: 2014,
      make: "Honda",
      model: "Accord EX-L",
      date: "Today"
    },
    {
      vin: "5FNRL6H58NB028461",
      year: 2022,
      make: "Honda",
      model: "Odyssey EX-L",
      date: "Yesterday"
    },
    {
      vin: "1C4RJFBG8NC139802",
      year: 2022,
      make: "Jeep",
      model: "Grand Cherokee",
      date: "3/12/2025"
    }
  ];
  
  // Current vehicle data
  const vehicleData = {
    basic: {
      year: 2014,
      make: "HONDA",
      model: "ACCORD EX-L",
      vin: "1HGCR2F81EA036925",
      engine: "2.4L I4 F DOHC 16V",
      transmission: "FRONT WHEEL DRIVE",
      fuelType: "GASOLINE",
      mileage: 277502,
      color: "White"
    },
    values: {
      retail: 5180,
      privateParty: 4110,
      tradeIn: 2710,
      estimatedProfit: 2470
    },
    score: {
      overall: 68,
      condition: 61,
      history: 85,
      market: 65
    },
    history: {
      owners: 4,
      accidents: 0,
      serviceRecords: 31,
      lastService: "Oil change at 260,707 miles",
      lastEmissions: "Passed 03/13/2025"
    },
    comparableListings: [
      {
        price: 5795,
        miles: 221345,
        location: "Atlanta, GA",
        daysOnMarket: 23
      },
      {
        price: 4999,
        miles: 242578,
        location: "Marietta, GA",
        daysOnMarket: 45
      },
      {
        price: 5450,
        miles: 235670,
        location: "Roswell, GA",
        daysOnMarket: 18
      }
    ]
  };

  // Timeline data for history
  const timelineData = [
    { 
      date: "10/2013 - 09/2017", 
      event: "Owner 1", 
      details: "Purchased new in PA, regular dealer maintenance",
      mileage: "0 - 101,583"
    },
    { 
      date: "09/2017 - 03/2023", 
      event: "Owner 2", 
      details: "Owned in WV, maintained at Jay's Automotive",
      mileage: "101,583 - 228,675"
    },
    { 
      date: "03/2023", 
      event: "Owner 3", 
      details: "Brief ownership in PA (8 days)",
      mileage: "228,675"
    },
    { 
      date: "03/2023 - Present", 
      event: "Owner 4", 
      details: "Owned in GA, regularly serviced",
      mileage: "228,675 - 277,502"
    }
  ];
  
  // Handle scan form submission
  const handleScanSubmit = (e) => {
    e.preventDefault();
    // In a real app, this would query the API with the VIN
    // For demo purposes, we'll just navigate to the vehicle detail
    setActiveScreen('home');
  };
  
  return (
    <div className="flex flex-col h-full bg-gray-100">
      {/* App Header */}
      <div className="px-4 py-3 bg-blue-700 text-white shadow-md">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="font-bold text-xl">DealerInsight</div>
          </div>
          <div className="flex items-center space-x-3">
            <div className="relative">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z"></path>
              </svg>
              <div className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                {dealerInfo.notifications}
              </div>
            </div>
            <div className="w-8 h-8 rounded-full bg-blue-800 flex items-center justify-center text-sm font-semibold">
              {dealerInfo.dealershipLogo}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 overflow-auto">
        {/* Home Screen (Vehicle Details) */}
        {activeScreen === 'home' && (
          <div>
            {/* Vehicle Quick Card */}
            <div className="px-4 py-3 bg-white shadow-md border-b border-gray-200">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-gray-900 text-lg font-bold">{vehicleData.basic.year} {vehicleData.basic.make} {vehicleData.basic.model}</h2>
                  <p className="text-gray-600 text-sm">VIN: {vehicleData.basic.vin.slice(-8)}</p>
                </div>
                <div className="bg-blue-100 text-blue-800 rounded-md px-2 py-1 font-semibold text-sm">
                  {vehicleData.score.overall}/100
                </div>
              </div>
            </div>

            {/* Tab Header */}
            <div className="flex border-b border-gray-200 bg-white">
              <button 
                className={`flex-1 py-3 text-sm font-medium ${activeTab === 'overview' ? 'text-blue-700 border-b-2 border-blue-700' : 'text-gray-500'}`}
                onClick={() => setActiveTab('overview')}
              >
                Overview
              </button>
              <button 
                className={`flex-1 py-3 text-sm font-medium ${activeTab === 'history' ? 'text-blue-700 border-b-2 border-blue-700' : 'text-gray-500'}`}
                onClick={() => setActiveTab('history')}
              >
                History
              </button>
              <button 
                className={`flex-1 py-3 text-sm font-medium ${activeTab === 'valuation' ? 'text-blue-700 border-b-2 border-blue-700' : 'text-gray-500'}`}
                onClick={() => setActiveTab('valuation')}
              >
                Valuation
              </button>
              <button 
                className={`flex-1 py-3 text-sm font-medium ${activeTab === 'market' ? 'text-blue-700 border-b-2 border-blue-700' : 'text-gray-500'}`}
                onClick={() => setActiveTab('market')}
              >
                Market
              </button>
            </div>

            {/* Tab Content */}
            <div className="p-4">
              {activeTab === 'overview' && (
                <div className="space-y-4">
                  {/* Vehicle Stats Card */}
                  <div className="bg-white rounded-lg shadow-md p-4">
                    <h3 className="text-sm text-gray-500 font-medium mb-3">VEHICLE SNAPSHOT</h3>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-xs text-gray-500">Mileage</p>
                        <p className="text-base font-semibold">{vehicleData.basic.mileage.toLocaleString()} mi</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Owners</p>
                        <p className="text-base font-semibold">{vehicleData.history.owners}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Service Records</p>
                        <p className="text-base font-semibold">{vehicleData.history.serviceRecords}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Last Service</p>
                        <p className="text-sm font-semibold">{vehicleData.history.lastService}</p>
                      </div>
                    </div>
                  </div>

                  {/* Vehicle Score Card */}
                  <div className="bg-white rounded-lg shadow-md p-4">
                    <h3 className="text-sm text-gray-500 font-medium mb-3">DEALERINSIGHT SCORE</h3>
                    <div className="flex items-center mb-4">
                      <div className="w-16 h-16 rounded-full flex items-center justify-center bg-blue-100 mr-4">
                        <span className="text-2xl font-bold text-blue-700">{vehicleData.score.overall}</span>
                      </div>
                      <div className="flex-1">
                        <p className="text-xs text-gray-500 mb-1">Vehicle Rating</p>
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-blue-700" 
                            style={{width: `${vehicleData.score.overall}%`}}
                          ></div>
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="bg-gray-50 p-2 rounded-md text-center">
                        <p className="text-xs text-gray-500">Condition</p>
                        <p className="text-lg font-semibold">{vehicleData.score.condition}</p>
                      </div>
                      <div className="bg-gray-50 p-2 rounded-md text-center">
                        <p className="text-xs text-gray-500">History</p>
                        <p className="text-lg font-semibold">{vehicleData.score.history}</p>
                      </div>
                      <div className="bg-gray-50 p-2 rounded-md text-center">
                        <p className="text-xs text-gray-500">Market</p>
                        <p className="text-lg font-semibold">{vehicleData.score.market}</p>
                      </div>
                    </div>
                  </div>

                  {/* Value Summary Card */}
                  <div className="bg-white rounded-lg shadow-md p-4">
                    <h3 className="text-sm text-gray-500 font-medium mb-3">VALUE SUMMARY</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm">Retail Value</span>
                        <span className="text-lg font-bold">${vehicleData.values.retail.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm">Private Party</span>
                        <span className="text-base font-semibold">${vehicleData.values.privateParty.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm">Trade-In Value</span>
                        <span className="text-base font-semibold">${vehicleData.values.tradeIn.toLocaleString()}</span>
                      </div>
                      <div className="pt-2 border-t border-gray-200">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium">Est. Profit Potential</span>
                          <span className="text-lg font-bold text-green-600">${vehicleData.values.estimatedProfit.toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex space-x-3">
                    <button className="flex-1 bg-blue-700 text-white py-3 px-4 rounded-md font-medium shadow-sm">
                      Save to Inventory
                    </button>
                    <button className="flex-1 bg-white border border-blue-700 text-blue-700 py-3 px-4 rounded-md font-medium shadow-sm">
                      Share Report
                    </button>
                  </div>
                </div>
              )}

              {activeTab === 'history' && (
                <div className="space-y-4">
                  {/* History Summary Card */}
                  <div className="bg-white rounded-lg shadow-md p-4">
                    <h3 className="text-sm text-gray-500 font-medium mb-3">HISTORY HIGHLIGHTS</h3>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="bg-green-50 p-3 rounded-md flex items-center">
                        <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center mr-3">
                          <svg className="w-5 h-5 text-green-600" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Accidents</p>
                          <p className="text-base font-semibold text-green-700">None Reported</p>
                        </div>
                      </div>
                      <div className="bg-green-50 p-3 rounded-md flex items-center">
                        <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center mr-3">
                          <svg className="w-5 h-5 text-green-600" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Title Status</p>
                          <p className="text-base font-semibold text-green-700">Clean</p>
                        </div>
                      </div>
                    </div>
                    <div className="pt-2">
                      <p className="text-xs text-gray-500 mb-1">Vehicle Rating</p>
                      <div className="flex items-center">
                        <div className="flex items-center mr-4">
                          <div className="w-10 h-10 rounded-full flex items-center justify-center bg-blue-100 mr-2">
                            <span className="text-lg font-bold text-blue-700">{vehicleData.score.history}</span>
                          </div>
                          <span className="text-sm font-medium">History Score</span>
                        </div>
                        <div className="flex-1">
                          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-blue-700" 
                              style={{width: `${vehicleData.score.history}%`}}
                            ></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Ownership Timeline Card */}
                  <div className="bg-white rounded-lg shadow-md p-4">
                    <h3 className="text-sm text-gray-500 font-medium mb-3">OWNERSHIP TIMELINE</h3>
                    <div className="space-y-4">
                      {timelineData.map((item, index) => (
                        <div key={index} className="relative pl-6 pb-4">
                          {index !== timelineData.length - 1 && (
                            <div className="absolute top-0 left-2 h-full w-0.5 bg-gray-200"></div>
                          )}
                          <div className="absolute top-0 left-0 w-4 h-4 rounded-full bg-blue-500"></div>
                          <div>
                            <p className="text-xs text-gray-500">{item.date}</p>
                            <p className="text-base font-semibold">{item.event}</p>
                            <p className="text-sm text-gray-600">{item.details}</p>
                            <p className="text-xs text-gray-500 mt-1">Mileage: {item.mileage}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Service History Card */}
                  <div className="bg-white rounded-lg shadow-md p-4">
                    <h3 className="text-sm text-gray-500 font-medium mb-3">SERVICE HIGHLIGHTS</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm">Total Service Records</span>
                        <span className="text-base font-semibold">{vehicleData.history.serviceRecords}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm">Last Service</span>
                        <span className="text-sm">{vehicleData.history.lastService}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm">Last Emissions</span>
                        <span className="text-sm text-green-600 font-semibold">{vehicleData.history.lastEmissions}</span>
                      </div>
                      <div className="pt-2">
                        <button className="w-full text-blue-700 border border-blue-700 py-2 px-4 rounded-md text-sm font-medium">
                          View All Service Records
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'valuation' && (
                <div className="space-y-4">
                  {/* Valuation Card */}
                  <div className="bg-white rounded-lg shadow-md p-4">
                    <h3 className="text-sm text-gray-500 font-medium mb-3">MARKET VALUES</h3>
                    <div className="space-y-6">
                      <div>
                        <div className="flex justify-between mb-1">
                          <span className="text-sm font-medium">Retail Value</span>
                          <span className="text-lg font-bold">${vehicleData.values.retail.toLocaleString()}</span>
                        </div>
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div className="h-full bg-blue-700 w-full"></div>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Suggested listing price for dealers</p>
                      </div>
                      
                      <div>
                        <div className="flex justify-between mb-1">
                          <span className="text-sm font-medium">Private Party</span>
                          <span className="text-lg font-bold">${vehicleData.values.privateParty.toLocaleString()}</span>
                        </div>
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div className="h-full bg-blue-500" style={{width: "80%"}}></div>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Expected price in private sale</p>
                      </div>
                      
                      <div>
                        <div className="flex justify-between mb-1">
                          <span className="text-sm font-medium">Trade-In Value</span>
                          <span className="text-lg font-bold">${vehicleData.values.tradeIn.toLocaleString()}</span>
                        </div>
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div className="h-full bg-blue-400" style={{width: "52%"}}></div>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Expected dealer acquisition cost</p>
                      </div>
                    </div>
                  </div>

                  {/* Value Factors Card */}
                  <div className="bg-white rounded-lg shadow-md p-4">
                    <h3 className="text-sm text-gray-500 font-medium mb-3">VALUE FACTORS</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center">
                          <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center mr-3">
                            <svg className="w-5 h-5 text-red-600" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <span className="text-sm">High Mileage</span>
                        </div>
                        <span className="text-sm text-red-600">-$2,200</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <div className="flex items-center">
                          <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center mr-3">
                            <svg className="w-5 h-5 text-green-600" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <span className="text-sm">Clean Title</span>
                        </div>
                        <span className="text-sm text-green-600">+$800</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <div className="flex items-center">
                          <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center mr-3">
                            <svg className="w-5 h-5 text-green-600" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <span className="text-sm">Service History</span>
                        </div>
                        <span className="text-sm text-green-600">+$500</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <div className="flex items-center">
                          <div className="w-8 h-8 rounded-full bg-yellow-100 flex items-center justify-center mr-3">
                            <svg className="w-5 h-5 text-yellow-600" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <span className="text-sm">Multiple Owners</span>
                        </div>
                        <span className="text-sm text-yellow-600">-$300</span>
                      </div>
                    </div>
                  </div>

                  {/* Profit Calculator Card */}
                  <div className="bg-white rounded-lg shadow-md p-4">
                    <h3 className="text-sm text-gray-500 font-medium mb-3">PROFIT CALCULATOR</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm">Retail Value</span>
                        <span className="text-base font-semibold">${vehicleData.values.retail.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm">Est. Acquisition Cost</span>
                        <span className="text-base font-semibold">${vehicleData.values.tradeIn.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm">Est. Reconditioning</span>
                        <span className="text-base font-semibold">$0</span>
                      </div>
                      <div className="pt-2 border-t border-gray-200">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium">Est. Gross Profit</span>
                          <span className="text-lg font-bold text-green-600">${vehicleData.values.estimatedProfit.toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'market' && (
                <div className="space-y-4">
                  {/* Market Analysis Card */}
                  <div className="bg-white rounded-lg shadow-md p-4">
                    <h3 className="text-sm text-gray-500 font-medium mb-3">MARKET ANALYSIS</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm">Avg. Days on Market</span>
                        <span className="text-base font-semibold">32 days</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm">Local Supply</span>
                        <div className="flex items-center">
                          <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                            <div className="bg-blue-700 h-2 rounded-full" style={{width: "40%"}}></div>
                          </div>
                          <span className="text-sm">Low</span>
                        </div>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm">Local Demand</span>
                        <div className="flex items-center">
                          <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                            <div className="bg-blue-700 h-2 rounded-full" style={{width: "75%"}}></div>
                          </div>
                          <span className="text-sm">High</span>
                        </div>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm">Price Trend (90 days)</span>
                        <span className="text-sm text-green-600">↑ 3.2%</span>
                      </div>
                    </div>
                  </div>

                  {/* Similar Listings Card */}
                  <div className="bg-white rounded-lg shadow-md p-4">
                    <h3 className="text-sm text-gray-500 font-medium mb-3">SIMILAR VEHICLES IN MARKET</h3>
                    <div className="space-y-4">
                      {vehicleData.comparableListings.map((listing, index) => (
                        <div key={index} className="p-3 border border-gray-200 rounded-md">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-base font-semibold">${listing.price.toLocaleString()}</span>
                            <span className="text-xs text-gray-500">{listing.daysOnMarket} days on market</span>
                          </div>
                          <div className="flex text-sm text-gray-600">
                            <span className="mr-3">{listing.miles.toLocaleString()} mi</span>
                            <span>{listing.location}</span>
                          </div>
                        </div>
                      ))}
                      <button className="w-full text-blue-700 border border-blue-700 py-2 px-4 rounded-md text-sm font-medium">
                        View All Comparable Listings
                      </button>
                    </div>
                  </div>

                  {/* Market Positioning Card */}
                  <div className="bg-white rounded-lg shadow-md p-4">
                    <h3 className="text-sm text-gray-500 font-medium mb-3">YOUR MARKET POSITION</h3>
                    <div className="mb-4">
                      <div className="flex justify-between mb-1">
                        <span className="text-xs text-gray-500">Lower Price</span>
                        <span className="text-xs text-gray-500">Higher Price</span>
                      </div>
                      <div className="h-8 bg-gray-200 rounded-full relative">
                        {/* Price position indicator */}
                        <div className="absolute top-0 left-0 right-0 bottom-0 flex items-center px-2">
                          <div className="flex-1 flex justify-center">
                            <div className="h-1 bg-gray-400 w-full relative">
                              <div 
                                className="absolute -top-3 w-6 h-6 rounded-full bg-blue-700 flex items-center justify-center text-white text-xs font-semibold"
                                style={{left: "38%"}}
                              >
                                You
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="text-sm text-gray-700">
                      <p>Your listing price is <span className="font-medium">competitive</span> compared to similar vehicles in your market. You're priced <span className="font-medium text-green-600">12% below</span> the average comparable listing.</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Inventory Screen */}
        {activeScreen === 'inventory' && (
          <div className="p-4">
            <div className="mb-4">
              <h2 className="text-xl font-bold text-gray-800">Inventory</h2>
              <p className="text-sm text-gray-500">Atlanta Motors • {inventoryData.length} vehicles</p>
            </div>
            
            {/* Inventory Filters */}
            <div className="bg-white rounded-lg shadow-md p-3 mb-4">
              <div className="flex space-x-2">
                <button className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                  All
                </button>
                <button className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
                  Recent
                </button>
                <button className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
                  Price ▼
                </button>
                <button className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
                  Days ▲
                </button>
              </div>
            </div>
            
            {/* Inventory List */}
            <div className="space-y-3">
              {inventoryData.map((vehicle) => (
                <div key={vehicle.id} className={`bg-white rounded-lg shadow-md p-4 ${vehicle.isActive ? 'border-2 border-blue-500' : ''}`}>
                  <div className="flex">
                    <div className="w-16 h-16 bg-gray-200 rounded-md flex items-center justify-center text-xl font-bold text-gray-500 mr-4">
                      {vehicle.image}
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between">
                        <div>
                          <h3 className="text-gray-900 font-semibold">{vehicle.year} {vehicle.make} {vehicle.model}</h3>
                          <p className="text-sm text-gray-500">{vehicle.mileage.toLocaleString()} miles</p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold">${vehicle.price.toLocaleString()}</p>
                          <p className="text-xs text-gray-500">{vehicle.daysInInventory} days in stock</p>
                        </div>
                      </div>
                      <div className="flex justify-between mt-2">
                        <p className="text-sm text-gray-600">Profit: <span className="text-green-600 font-medium">${vehicle.profit.toLocaleString()}</span></p>
                        <button className="text-sm text-blue-700 font-medium">Details →</button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Scan Screen */}
        {activeScreen === 'scan' && (
          <div className="p-4">
            <div className="mb-4">
              <h2 className="text-xl font-bold text-gray-800">Scan Vehicle</h2>
              <p className="text-sm text-gray-500">Enter VIN or license plate</p>
            </div>
            
            {/* Scan Input Form */}
            <div className="bg-white rounded-lg shadow-md p-4 mb-6">
              <form onSubmit={handleScanSubmit}>
                <div className="mb-4">
                  <div className="flex">
                    <input
                      type="text"
                      value={scanInput}
                      onChange={(e) => setScanInput(e.target.value)}
                      placeholder="Enter VIN (17 characters)"
                      className="flex-1 border border-gray-300 rounded-l-md px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button 
                      type="button"
                      className="bg-blue-100 text-blue-700 px-3 rounded-r-md border border-blue-200 border-l-0"
                    >
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z"></path>
                        <path d="M8 11a1 1 0 100-2 1 1 0 000 2zm0-4a1 1 0 100-2 1 1 0 000 2zm0 8a1 1 0 100-2 1 1 0 000 2z"></path>
                      </svg>
                    </button>
                  </div>
                  <div className="flex justify-center mt-2">
                    <button 
                      type="button"
                      className="flex items-center text-blue-700 text-sm font-medium mx-2"
                    >
                      <svg className="w-5 h-5 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4 5a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V7a2 2 0 00-2-2h-1.586a1 1 0 01-.707-.293l-1.121-1.121A2 2 0 0011.172 3H8.828a2 2 0 00-1.414.586L6.293 4.707A1 1 0 015.586 5H4zm6 9a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd"></path>
                      </svg>
                      Camera
                    </button>
                    <button 
                      type="button"
                      className="flex items-center text-blue-700 text-sm font-medium mx-2"
                    >
                      <svg className="w-5 h-5 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z"></path>
                        <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd"></path>
                      </svg>
                      Scan Document
                    </button>
                  </div>
                </div>
                
                <button 
                  type="submit"
                  className="w-full bg-blue-700 text-white py-3 px-4 rounded-md font-medium shadow-sm"
                >
                  Lookup Vehicle
                </button>
              </form>
            </div>
            
            {/* Recent Scans */}
            <div>
              <h3 className="text-lg font-semibold mb-3 text-gray-800">Recent Scans</h3>
              <div className="space-y-3">
                {recentScans.map((scan, index) => (
                  <div key={index} className="bg-white rounded-lg shadow-md p-3 flex justify-between items-center">
                    <div>
                      <p className="font-medium">{scan.year} {scan.make} {scan.model}</p>
                      <p className="text-xs text-gray-500">VIN: {scan.vin.slice(-8)}</p>
                    </div>
                    <div className="flex items-center">
                      <span className="text-xs text-gray-500 mr-3">{scan.date}</span>
                      <button className="text-blue-700">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd"></path>
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        
        {/* Profile Screen */}
        {activeScreen === 'profile' && (
          <div className="p-4">
            {/* Profile Header */}
            <div className="bg-white rounded-lg shadow-md p-4 mb-4">
              <div className="flex items-center">
                <div className="w-16 h-16 rounded-full bg-blue-700 flex items-center justify-center text-white text-xl font-bold mr-4">
                  {dealerInfo.profileImage}
                </div>
                <div>
                  <h2 className="text-xl font-bold">{dealerInfo.name}</h2>
                  <p className="text-gray-600">{dealerInfo.role}</p>
                  <p className="text-sm text-gray-500">{dealerInfo.dealership} • {dealerInfo.location}</p>
                </div>
              </div>
            </div>
            
            {/* Performance Stats */}
            <div className="bg-white rounded-lg shadow-md p-4 mb-4">
              <h3 className="text-sm text-gray-500 font-medium mb-3">THIS MONTH'S PERFORMANCE</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 p-3 rounded-md">
                  <p className="text-xs text-gray-500">Vehicles Sold</p>
                  <p className="text-2xl font-bold">{dealerInfo.salesThisMonth}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-md">
                  <p className="text-xs text-gray-500">Total Profit</p>
                  <p className="text-2xl font-bold text-green-600">{dealerInfo.profitThisMonth}</p>
                </div>
              </div>
            </div>
            
            {/* Account Settings */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200">
                <h3 className="font-medium">Account Settings</h3>
              </div>
              <div>
                <a href="#" className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-gray-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0010 11z" clipRule="evenodd"></path>
                    </svg>
                    <span>Personal Information</span>
                  </div>
                  <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd"></path>
                  </svg>
                </a>
                <a href="#" className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-gray-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd"></path>
                    </svg>
                    <span>App Preferences</span>
                  </div>
                  <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd"></path>
                  </svg>
                </a>
                <a href="#" className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-gray-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd"></path>
                    </svg>
                    <span>Privacy & Security</span>
                  </div>
                  <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd"></path>
                  </svg>
                </a>
                <a href="#" className="flex items-center justify-between px-4 py-3">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-gray-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd"></path>
                    </svg>
                    <span>Help & Support</span>
                  </div>
                  <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd"></path>
                  </svg>
                </a>
              </div>
            </div>
            
            {/* Sign Out Button */}
            <div className="mt-6">
              <button className="w-full border border-red-600 text-red-600 py-3 px-4 rounded-md font-medium">
                Sign Out
              </button>
            </div>
          </div>
        )}
      </div>
      
      {/* Bottom Navigation */}
      <div className="bg-white border-t border-gray-200 grid grid-cols-5">
        <button 
          className={`flex flex-col items-center justify-center p-2 ${activeScreen === 'home' ? 'text-blue-700' : 'text-gray-500'}`}
          onClick={() => setActiveScreen('home')}
        >
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
            <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z"></path>
          </svg>
          <span className="text-xs mt-1 font-medium">Home</span>
        </button>
        <button 
          className={`flex flex-col items-center justify-center p-2 ${activeScreen === 'inventory' ? 'text-blue-700' : 'text-gray-500'}`}
          onClick={() => setActiveScreen('inventory')}
        >
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
            <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"></path>
          </svg>
          <span className="text-xs mt-1 font-medium">Inventory</span>
        </button>
        <button 
          className={`flex flex-col items-center justify-center p-2 ${activeScreen === 'scan' ? 'text-blue-700' : 'text-gray-500'}`}
          onClick={() => setActiveScreen('scan')}
        >
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M3 4a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm2 2V5h1v1H5zM3 13a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1H4a1 1 0 01-1-1v-3zm2 2v-1h1v1H5zM13 3a1 1 0 00-1 1v3a1 1 0 001 1h3a1 1 0 001-1V4a1 1 0 00-1-1h-3zm1 2v1h1V5h-1z" clipRule="evenodd" />
            <path d="M11 4a1 1 0 10-2 0v1a1 1 0 002 0V4zM10 7a1 1 0 011 1v1h2a1 1 0 110 2h-3a1 1 0 01-1-1V8a1 1 0 011-1zM16 9a1 1 0 100 2 1 1 0 000-2zM9 13a1 1 0 011-1h1a1 1 0 110 2v2a1 1 0 11-2 0v-3zM7 11a1 1 0 100-2H4a1 1 0 100 2h3z" />
          </svg>
          <span className="text-xs mt-1 font-medium">Scan</span>
        </button>
        <button 
          className={`flex flex-col items-center justify-center p-2 ${activeScreen === 'profile' ? 'text-blue-700' : 'text-gray-500'}`}
          onClick={() => setActiveScreen('profile')}
        >
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd"></path>
          </svg>
          <span className="text-xs mt-1 font-medium">Profile</span>
        </button>
        <button 
          className="flex flex-col items-center justify-center p-2 text-gray-500"
        >
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd"></path>
          </svg>
          <span className="text-xs mt-1 font-medium">Settings</span>
        </button>
      </div>
    </div>
  );
};

export default DealerInsightApp;